import { FC, useState, useRef, MutableRefObject, ReactNode, KeyboardEvent } from 'react';
import { DataInline, DropDownInlinePropsType } from '../types/propsType';
import FAIcon, { IconStyle } from '../../Icons/Icons';
import { EVENT_KEY, Theme } from '../../../types/enums';
import { useClickOutsideDetector } from '../../../hooks';
import { useTranslation } from '../../Translation/TranslationProvider';
import { NextgenImage } from '../../NextgenImage/NextgenImage';
import styles from './inlineDropDown.module.scss';

/**
 * @param configData should be passed with `data` to configure display and operations.
 * @param name should unique to differentiate element on screen
 */
const InlineDropDown: FC<DropDownInlinePropsType> = ({
  dropdownLabel,
  disabled,
  theme = Theme.PURPLE,
  data = [],
  configData,
  testId = 'inline-item',
  value,
  ariaLabel,
  className,
}: DropDownInlinePropsType) => {
  const t = useTranslation();
  const [drawerDisplay, setDrawerDisplay] = useState(false);
  const dropdownRef = useRef() as MutableRefObject<HTMLDivElement>;

  const ariaLabelValue = ariaLabel || t('nextgen.Dropdown.ariaLabel');

  useClickOutsideDetector(dropdownRef, () => setDrawerDisplay(false));
  const eventObject = {
    onClick: () => setDrawerDisplay(!drawerDisplay),
    onKeyDown: (event: KeyboardEvent<HTMLSpanElement>) =>
      event.key === EVENT_KEY.ENTER && setDrawerDisplay(!drawerDisplay),
  };
  const selectHandler = (item: DataInline) => {
    if (configData?.disableItemKey && item[configData?.disableItemKey]) {
      return;
    }
    configData?.onSelect(item);
    setDrawerDisplay(false);
  };
  const listItemClassName = (item: DataInline) =>
    `${styles['drawer-list-item']} ${value === item[`${configData?.label}`] ? styles['selected-item'] : ''} ${
      item.isDisabled ? styles['drawer-item-disabled'] : ''
    }`;
  return (
    <section
      className={`${styles['inline-dropdown-container']} ${styles[theme]} ${disabled ? styles['disabled'] : ''} ${
        drawerDisplay ? styles['active'] : ''
      } ${className}`}
      ref={dropdownRef}
      data-testid="inline-dropdown-test-id"
    >
      <section className={styles['inline-dropdown-main']}>
        <span {...eventObject} data-testid="dropdown-label-testid">
          {dropdownLabel}
        </span>
        <section className={styles['inline-drawer-container']}>
          <FAIcon
            iconName="caret-down"
            iconStyle={IconStyle.SOLID}
            className={styles['dropdown-icon']}
            data-testid="inline-dropdown-icon"
            {...eventObject}
          />
          <section className={styles['inline-dropdown-drawer']}>
            <span>
              <ul className={styles['drawer-list']}>
                {data?.map((item: DataInline, index: number) => (
                  <li
                    className={listItemClassName(item)}
                    data-testid={`${testId}-result-test-id`}
                    role="presentation"
                    key={`dropdown-inline-${+index}`}
                    onClick={() => selectHandler(item)}
                    aria-label={ariaLabelValue}
                    onKeyDown={() => selectHandler(item)}
                  >
                    <span className={styles['item-label']}>
                      {item.imgSrc ? (
                        <div className={styles['item-label-image']}>
                          <NextgenImage src={item.imgSrc} alt="" />
                        </div>
                      ) : (
                        ''
                      )}
                      {item[`${configData?.label}`] as ReactNode}
                    </span>
                    {value === item[`${configData?.label}`] ? (
                      <FAIcon iconName="check" className={styles['check-icon']} />
                    ) : (
                      <div className={styles['placeholder-icon']} />
                    )}
                  </li>
                ))}
              </ul>
            </span>
          </section>
        </section>
      </section>
    </section>
  );
};
export default InlineDropDown;
